import { isPlatformBrowser } from '@angular/common';
import { InjectionToken, PLATFORM_ID, inject } from '@angular/core';
import {
  Analytics,
  getAnalytics,
  initializeAnalytics
} from '@angular/fire/analytics';
import { FirebaseApp, getApp, initializeApp } from '@angular/fire/app';
import {
  AppCheck,
  ReCaptchaV3Provider,
  initializeAppCheck
} from '@angular/fire/app-check';
import { Auth, connectAuthEmulator, getAuth } from '@angular/fire/auth';
import { browserLocalPersistence, setPersistence } from 'firebase/auth';
import {
  Firestore,
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore
} from '@angular/fire/firestore';
import {
  Functions,
  HttpsCallableOptions,
  HttpsCallableResult,
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable
} from '@angular/fire/functions';
import { RemoteConfig, getRemoteConfig } from '@angular/fire/remote-config';
import {
  Storage,
  connectStorageEmulator,
  getStorage
} from '@angular/fire/storage';
import { environment } from './environment';

// Initialize Firebase app in injection context
export const FIREBASE_APP = new InjectionToken<FirebaseApp>('Firebase app', {
  providedIn: 'root',
  factory: () => {
    try {
      return getApp();
    } catch {
      const app = initializeApp(environment.firebaseConfig);
      // Disable App Check verification in emulator mode
      if (environment.useEmulators) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (app as any).automaticDataCollectionEnabled = false;
      }
      return app;
    }
  }
});

export const AUTH = new InjectionToken<Auth>('Firebase auth', {
  providedIn: 'root',
  factory: () => {
    const app = inject(FIREBASE_APP);
    const platformId = inject(PLATFORM_ID);
    const auth = getAuth(app);

    if (isPlatformBrowser(platformId)) {
      // Only set persistence in browser environment
      setPersistence(auth, browserLocalPersistence).catch((error) => {
        console.warn('Error setting auth persistence:', error);
      });
    }

    if (environment.useEmulators) {
      connectAuthEmulator(auth, 'http://localhost:9099', {
        disableWarnings: true
      });
    }
    return auth;
  }
});

// Store Firestore instance outside factory to ensure singleton
let firestoreInstance: Firestore | null = null;
let emulatorConnected = false;

export const FIRESTORE = new InjectionToken<Firestore>('Firebase firestore', {
  providedIn: 'root',
  factory: () => {
    const app = inject(FIREBASE_APP);
    const platformId = inject(PLATFORM_ID);
    const isServer = !isPlatformBrowser(platformId);

    if (firestoreInstance) {
      return firestoreInstance;
    }

    const settings = environment.useEmulators
      ? { experimentalForceLongPolling: true }
      : {
          ignoreUndefinedProperties: true,
          // SSR/Node.js: gRPC can hang — use long polling for reliable Firestore access
          ...(isServer && { experimentalForceLongPolling: true })
        };

    // initializeFirestore first to apply settings (long polling for SSR).
    // Falls back to getFirestore if already initialized (e.g., HMR).
    try {
      firestoreInstance = initializeFirestore(app, settings);
    } catch {
      firestoreInstance = getFirestore(app);
    }

    if (environment.useEmulators && !emulatorConnected) {
      connectFirestoreEmulator(firestoreInstance, 'localhost', 8080);
      emulatorConnected = true;
    }

    return firestoreInstance;
  }
});

export const FUNCTIONS = new InjectionToken<Functions>('Firebase functions', {
  providedIn: 'root',
  factory: () => {
    const app = inject(FIREBASE_APP);
    const functions = getFunctions(app);

    if (environment.useEmulators) {
      connectFunctionsEmulator(functions, 'localhost', 5001);

      // Disable App Check for emulator
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const functionsInstance = functions as any;
      functionsInstance._config = {
        ...(functionsInstance._config || {}),
        requireAppCheck: false,
        validateAppCheck: false
      };

      // Override internal verification methods
      functionsInstance._verifyAppCheck = async () => true;
      functionsInstance._verifyAuth = async () => true;
    }

    return functions;
  }
});

export const STORAGE = new InjectionToken<Storage>('Firebase storage', {
  providedIn: 'root',
  factory: () => {
    const app = inject(FIREBASE_APP);
    const storage = getStorage(app);
    if (environment.useEmulators) {
      connectStorageEmulator(storage, 'localhost', 9199);
    }
    return storage;
  }
});

export const ANALYTICS = new InjectionToken<Analytics | null>(
  'Firebase analytics',
  {
    providedIn: 'root',
    factory: () => {
      const app = inject(FIREBASE_APP);
      const platformId = inject(PLATFORM_ID);

      if (!isPlatformBrowser(platformId)) {
        return null;
      }

      try {
        // Disable automatic page_view — we send manual page_view events
        // with explicit page_location to guarantee UTM campaign attribution.
        // See: Ga4Service.startPageViewTracking()
        return initializeAnalytics(app, {
          config: { send_page_view: false }
        });
      } catch {
        // Analytics already initialized (e.g., HMR) — get existing instance
        try {
          return getAnalytics(app);
        } catch {
          return null;
        }
      }
    }
  }
);

export const REMOTE_CONFIG = new InjectionToken<RemoteConfig>(
  'Firebase remote config',
  {
    providedIn: 'root',
    factory: () => {
      const app = inject(FIREBASE_APP);
      return getRemoteConfig(app);
    }
  }
);

/**
 * Lazy App Check initialization singleton.
 * reCAPTCHA script is only loaded when this is first called, not at app startup.
 * This improves initial page load by ~350KB.
 */
let appCheckInstance: AppCheck | null = null;
let appCheckInitPromise: Promise<AppCheck> | null = null;

/**
 * Initializes Firebase App Check lazily.
 * Call this before making any Firebase Function calls.
 */
export async function initializeAppCheckLazy(
  app: FirebaseApp,
  platformId: object | string
): Promise<AppCheck> {
  // Return existing instance
  if (appCheckInstance) {
    return appCheckInstance;
  }

  // Return in-progress initialization
  if (appCheckInitPromise) {
    return appCheckInitPromise;
  }

  appCheckInitPromise = new Promise((resolve) => {
    if (environment.useEmulators) {
      appCheckInstance = {
        app,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        activate: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        setTokenAutoRefreshEnabled: () => {},
        getToken: async () => ({
          token: 'emulator-bypass-token',
          expireTimeMillis: Date.now() + 3600000
        })
      } as unknown as AppCheck;
      resolve(appCheckInstance);
      return;
    }

    if (!isPlatformBrowser(platformId)) {
      appCheckInstance = {
        app,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        activate: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        setTokenAutoRefreshEnabled: () => {},
        getToken: async () => ({
          token: 'ssr-mock-token',
          expireTimeMillis: Date.now() + 3600000
        })
      } as unknown as AppCheck;
      resolve(appCheckInstance);
      return;
    }

    // Set debug token for non-production
    if (!environment.production) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN =
        environment.recaptcha.appCheckDebugToken;
    }

    // Initialize App Check - this loads the reCAPTCHA script
    appCheckInstance = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(environment.recaptcha.siteKey),
      isTokenAutoRefreshEnabled: true
    });
    resolve(appCheckInstance);
  });

  return appCheckInitPromise;
}

/**
 * Ensures App Check is initialized before making Cloud Function calls.
 * Call this before any httpsCallable that has enforceAppCheck: true.
 *
 * This function can be called outside of Angular injection context.
 * It uses getApp() to get the Firebase app instance.
 */
export async function ensureAppCheckInitialized(): Promise<void> {
  // If already initialized, return immediately
  if (appCheckInstance) {
    return;
  }

  // Get the Firebase app (should already be initialized)
  const app = getApp();

  // Detect platform: Angular's isPlatformBrowser expects 'browser' or 'server' string
  // Using 'browser' when document exists, 'server' otherwise
  const platformId = typeof document !== 'undefined' ? 'browser' : 'server';

  await initializeAppCheckLazy(app, platformId);
}

/**
 * Error codes that indicate transient failures and should trigger retry.
 */
const RETRYABLE_CALLABLE_ERROR_CODES = [
  'functions/unavailable',
  'functions/deadline-exceeded',
  'functions/internal',
  'functions/resource-exhausted',
  'app-check/fetch-status-error',
  'app-check/token-refresh-error'
] as const;

/**
 * Options for safeHttpsCallable, extending Firebase's HttpsCallableOptions.
 */
export interface SafeHttpsCallableOptions extends HttpsCallableOptions {
  /** Enable retry logic for transient failures. Default: true */
  enableRetry?: boolean;
  /** Maximum number of retry attempts. Default: 3 */
  maxRetries?: number;
  /** Base delay in milliseconds for exponential backoff. Default: 1000 */
  baseDelayMs?: number;
  /** Maximum delay in milliseconds between retries. Default: 8000 */
  maxDelayMs?: number;
}

/**
 * Check if an error is retryable for Cloud Functions.
 */
function isRetryableCallableError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const code = (error as { code?: string }).code;
  if (!code) return false;

  return RETRYABLE_CALLABLE_ERROR_CODES.some(
    (retryableCode) => code.includes(retryableCode) || code === retryableCode
  );
}

/**
 * Calculate exponential backoff delay with jitter.
 */
function calculateCallableBackoffDelay(
  attempt: number,
  baseDelayMs: number,
  maxDelayMs: number
): number {
  const exponentialDelay = baseDelayMs * Math.pow(2, attempt);
  const jitter = exponentialDelay * 0.25 * Math.random();
  return Math.min(exponentialDelay + jitter, maxDelayMs);
}

/**
 * Safe wrapper for httpsCallable that ensures App Check is initialized
 * before making Cloud Function calls.
 *
 * This prevents race conditions where the function is called before
 * the App Check token is ready, which causes 401 errors.
 *
 * @param functions - Firebase Functions instance
 * @param name - Name of the Cloud Function to call
 * @param options - Optional configuration including retry settings
 * @returns A callable function that returns a Promise with the result
 *
 * @example
 * ```typescript
 * // Basic usage (same as httpsCallable)
 * const myFunction = safeHttpsCallable<RequestType, ResponseType>(
 *   functionsService,
 *   'myFunctionName'
 * );
 * const result = await myFunction({ data: 'value' });
 *
 * // With retry disabled
 * const myFunction = safeHttpsCallable<RequestType, ResponseType>(
 *   functionsService,
 *   'myFunctionName',
 *   { enableRetry: false }
 * );
 * ```
 */
export function safeHttpsCallable<
  RequestData = unknown,
  ResponseData = unknown
>(
  functions: Functions,
  name: string,
  options?: SafeHttpsCallableOptions
): (data?: RequestData) => Promise<HttpsCallableResult<ResponseData>> {
  const {
    enableRetry = true,
    maxRetries = 3,
    baseDelayMs = 1000,
    maxDelayMs = 8000,
    ...httpsCallableOptions
  } = options ?? {};

  return async (
    data?: RequestData
  ): Promise<HttpsCallableResult<ResponseData>> => {
    // Ensure App Check is initialized before calling
    await ensureAppCheckInitialized();

    const callable = httpsCallable<RequestData, ResponseData>(
      functions,
      name,
      httpsCallableOptions
    );

    if (!enableRetry) {
      return callable(data);
    }

    let lastError: unknown = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await callable(data);
      } catch (error) {
        lastError = error;

        // Don't retry on the last attempt or for non-retryable errors
        if (!isRetryableCallableError(error) || attempt === maxRetries) {
          throw error;
        }

        const delay = calculateCallableBackoffDelay(
          attempt,
          baseDelayMs,
          maxDelayMs
        );
        console.warn(
          `[safeHttpsCallable] Retry ${attempt + 1}/${maxRetries} for ${name}. ` +
            `Error: ${(error as { code?: string }).code}. Retrying in ${Math.round(delay)}ms...`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    throw lastError;
  };
}

/**
 * Lazy App Check injection token.
 *
 * Returns a proxy that defers reCAPTCHA script loading (~350 KiB) until
 * the first Firebase Function call, rather than loading at app startup.
 *
 * On first access to getToken(), the real App Check is initialized.
 */
export const APP_CHECK = new InjectionToken<AppCheck>('Firebase app check', {
  providedIn: 'root',
  factory: () => {
    const app = inject(FIREBASE_APP);
    const platformId = inject(PLATFORM_ID);

    if (environment.useEmulators) {
      // Return a no-op AppCheck for emulators
      return {
        app,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        activate: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        setTokenAutoRefreshEnabled: () => {},
        getToken: async () => ({
          token: 'emulator-bypass-token',
          expireTimeMillis: Date.now() + 3600000 // 1 hour from now
        })
      } as unknown as AppCheck;
    }

    // Return a mock AppCheck for server-side rendering
    if (!isPlatformBrowser(platformId)) {
      return {
        app,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        activate: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        setTokenAutoRefreshEnabled: () => {},
        getToken: async () => ({
          token: 'ssr-mock-token',
          expireTimeMillis: Date.now() + 3600000
        })
      } as unknown as AppCheck;
    }

    // For browser: Return a lazy proxy that initializes on first use
    // This defers reCAPTCHA loading from app startup to first API call
    return {
      app,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      activate: () => {},
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      setTokenAutoRefreshEnabled: () => {},
      getToken: async (forceRefresh?: boolean) => {
        // Initialize real App Check on first token request
        const realAppCheck = await initializeAppCheckLazy(app, platformId);
        // Get token from the real instance
        const { getToken: getAppCheckToken } = await import(
          '@angular/fire/app-check'
        );
        return getAppCheckToken(realAppCheck, forceRefresh);
      }
    } as unknown as AppCheck;
  }
});
